import {
  addBusinessDays,
  isBusinessDay,
  todayParis,
} from "../utils/calendar.mjs";
export const resultGroups = {
  "Opportunités commerciales": [
    "Nouveau besoin",
    "Besoin futur",
    "Demande de devis",
    "Devis envoyé",
    "Devis accepté",
    "Rendez-vous demandé",
    "Rendez-vous obtenu",
    "Intéressé / à recontacter",
    "Plaquette envoyée",
    "Client à réactiver",
  ],
  "Absence de besoin et objections": [
    "Pas de besoin actuellement",
    "Activité en baisse",
    "Gel des recrutements",
    "Recrutement uniquement en interne",
    "Ne travaille pas avec l’intérim",
    "Travaille avec un concurrent",
    "Contrat exclusif avec un concurrent",
    "Tarifs trop élevés",
    "Conditions commerciales refusées",
    "Budget insuffisant",
    "Refus de collaboration",
    "Décision reportée",
  ],
  "Contact et communication": [
    "À rappeler",
    "Pas de réponse",
    "Message vocal laissé",
    "Standard / barrage secrétaire",
    "Mauvais interlocuteur",
    "Contact parti",
    "Nouveau décideur identifié",
    "Mauvais numéro",
    "Numéro hors service",
    "Email invalide",
    "Email envoyé",
    "En attente de réponse",
    "Contact absent / congés",
  ],
  "Exclusion / situation entreprise": [
    "Agence d’intérim",
    "Cabinet de recrutement",
    "Société de placement",
    "Entreprise en liquidation judiciaire",
    "Entreprise en redressement judiciaire",
    "Entreprise radiée",
    "Entreprise définitivement fermée",
    "Établissement fermé",
    "Entreprise déménagée",
    "Entreprise hors zone",
    "Entreprise hors cible",
    "Doublon CRM",
    "Données erronées",
    "Entreprise à vérifier",
  ],
  "Autres résultats existants": [
    "Pas de réponse / Non joignable",
    "Insatisfaction",
    "Client perdu",
  ],
};
export const v2ResultOptions = [...new Set(Object.values(resultGroups).flat())];
export const defaultCallRules = {
  quoteDays: 2,
  brochureDays: 3,
  unansweredDays: 2,
  noNeedDays: 30,
  decliningDays: 60,
  maxAttempts: 6,
  futureLeadDays: 7,
};
export const commercialStatuses = [
  "Nouveau prospect",
  "À qualifier",
  "En prospection",
  "À relancer",
  "En veille",
  "Opportunité ouverte",
  "Devis en cours",
  "Devis envoyé",
  "Client actif",
  "Client inactif",
  "Client perdu",
  "Exclu",
  "À vérifier",
];
export const isBlockedForProspecting = (client) =>
  Boolean(
    client?.prospectingOpposition ||
    client?.prospectingExcluded ||
    client?.engagementBlocked ||
    client?.commercialStatus === "Exclu",
  );
const excluded = [
  "Agence d’intérim",
  "Cabinet de recrutement",
  "Société de placement",
  "Entreprise radiée",
  "Entreprise définitivement fermée",
  "Établissement fermé",
  "Entreprise hors zone",
  "Entreprise hors cible",
];
const unanswered = [
  "Pas de réponse",
  "Pas de réponse / Non joignable",
  "Message vocal laissé",
  "Standard / barrage secrétaire",
];
const key = (v) =>
  String(v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
export function callRules(state) {
  const rules = { ...defaultCallRules };
  for (const [name, value] of Object.entries(state.automationRules || {}))
    if (
      name in rules &&
      Number.isInteger(Number(value)) &&
      Number(value) >= 1 &&
      Number(value) <= 365
    )
      rules[name] = Number(value);
  return rules;
}
export function validateCallV2(state, clientId, label, data = {}) {
  const client = state.clients.find((c) => c.id === clientId);
  if (!client) throw new Error("Établissement introuvable.");
  if (!v2ResultOptions.includes(label)) throw new Error("Résultat inconnu.");
  if (isBlockedForProspecting(client))
    throw new Error(
      "Cet établissement est bloqué pour la prospection. Une validation habilitée est nécessaire.",
    );
  if (state.pendingResultClientId && state.pendingResultClientId !== clientId)
    throw new Error("Terminez d’abord le même appel.");
  if (
    label === "À rappeler" && !data.doNotRecall &&
    (!/^\d{4}-\d{2}-\d{2}$/.test(data.due || "") ||
      !/^\d{2}:\d{2}$/.test(data.time || ""))
  )
    throw new Error("La date et l’heure du rappel sont obligatoires.");
  if (
    label === "Besoin futur" &&
    !data.futureDate &&
    !String(data.futurePeriod || "").trim()
  )
    throw new Error("Indiquez une date ou une période pour le besoin futur.");
  if (!data.doNotRecall && label === "Rendez-vous obtenu" && (!data.due || !data.time))
    throw new Error("La date et l’heure du rendez-vous sont obligatoires.");
  if (
    label === "Refus de collaboration" &&
    !["temporaire", "définitif"].includes(data.refusalType)
  )
    throw new Error("Précisez si le refus est temporaire ou définitif.");
  if (label === "Plaquette envoyée" && !data.channel)
    throw new Error("Indiquez le canal d’envoi de la plaquette.");
  if (
    data.qty !== "" &&
    data.qty != null &&
    (!Number.isInteger(Number(data.qty)) || Number(data.qty) < 1)
  )
    throw new Error(
      "Le nombre de personnes doit être un entier positif, ou rester vide.",
    );
}
export function applyCallV2(
  state,
  next,
  clientId,
  label,
  data = {},
  now = new Date(),
) {
  const before = state.clients.find((c) => c.id === clientId),
    today = todayParis(now),
    stamp = new Date(now).toISOString();
  const rules = callRules(state),
    closures = state.calendar?.closures || state.closures || [];
  if (
    data.operationId &&
    (state.results || []).some((r) => r.operationId === data.operationId)
  )
    return state;
  const result =
    next.results?.length > state.results.length
      ? next.results.at(-1)
      : {
          ...data,
          id: crypto.randomUUID(),
          clientId,
          label,
          createdAt: stamp,
          ownerId: before.ownerId,
          agency: before.agency,
          deciderJoined: !!data.deciderJoined,
        };
  if (label === "Agence d’intérim") result.deciderJoined = false;
  result.operationId = data.operationId || result.operationId;
  if (next === state)
    next = {
      ...state,
      results: [...state.results, result],
      pendingResultClientId: null,
      workLocks: (state.workLocks || []).filter((l) => l.clientId !== clientId),
      events: [
        ...(state.events || []),
        {
          id: crypto.randomUUID(),
          type: "call_result",
          clientId,
          label,
          text: `Résultat de mon appel : ${label}`,
          at: stamp,
          createdAt: stamp,
          resultId: result.id,
        },
      ],
    };
  let client = {
    ...next.clients.find((c) => c.id === clientId),
    lastCallResult: label,
    lastCallAt: stamp,
    lastContactAt: stamp,
  };
  let tasks = [...(next.tasks || [])];
  const task = (
    rule,
    title,
    days = rules.noNeedDays,
    category = "follow",
    extra = {},
  ) => {
    const explicit = extra.promised && data.due;
    const due = explicit
      ? data.due
      : data.due || addBusinessDays(today, days, closures);
    const scheduled = explicit ? due : addBusinessDays(due, 0, closures);
    const existing = tasks.find(
      (t) =>
        t.clientId === clientId &&
        !t.done &&
        !t.cancelled &&
        !t.manuallyModified && !t.manualEditedAt && !Number(t.postponement_count) &&
        t.automationKey === rule,
    );
    if (existing) {
      tasks = tasks.map((t) =>
        t.id === existing.id
          ? {
              ...t,
              due_date_original: scheduled,
              scheduled_date: scheduled,
              next_action_date: scheduled,
              resultId: result.id,
              calendarConflict: !!explicit && !isBusinessDay(due, closures),
              time: extra.promised ? data.time || "" : t.time,
              notes: data.notes || t.notes,
              needIds: [
                ...new Set(
                  [...(t.needIds || []), extra.needId].filter(Boolean),
                ),
              ],
            }
          : t,
      );
      client.nextAction = scheduled;
      client.next_action_date = scheduled;
      return;
    }
    tasks.push({
      id: crypto.randomUUID(),
      clientId,
      ownerId: before.ownerId,
      agency: before.agency,
      title,
      category,
      automationKey: rule,
      due_date_original: scheduled,
      scheduled_date: scheduled,
      time: extra.promised ? data.time || "" : "",
      calendarConflict: !!explicit && !isBusinessDay(due, closures),
      done: false,
      createdAt: stamp,
      notes: data.notes || "",
      resultId: result.id,
      ...extra,
    });
    client.nextAction = scheduled;
    client.next_action_date = scheduled;
  };
  // Replace the legacy generic follow-up from this call with one central rule.
  const oldIds = new Set(state.tasks.map((t) => t.id));
  tasks = tasks.filter((t) => oldIds.has(t.id) || t.category !== "follow");
  const preserveActive =
    String(before.status || "").toUpperCase() === "CLIENT ACTIF" ||
    before.commercialStatus === "Client actif";
  client.commercialStatus =
    before.commercialStatus ||
    (preserveActive
      ? "Client actif"
      : before.kind === "prospect"
        ? "En prospection"
        : "Client inactif");
  const setStatus = (status) => {
    if (!preserveActive || ["Exclu", "À vérifier"].includes(status))
      client.commercialStatus = status;
  };
  if (["Nouveau besoin", "Demande de devis"].includes(label) && next.needs.length === state.needs.length) {
    const missing = ["job", "qty", "type", "start", "duration"].filter(
      (field) => !data[field],
    );
    const need = {
      ...data,
      id: crypto.randomUUID(),
      clientId,
      establishment_id: clientId,
      client: before.name,
      job: data.job || "",
      qty: data.qty ? Number(data.qty) : null,
      type: data.type || "",
      status: "à qualifier",
      missingFields: missing,
      createdAt: stamp,
      ownerId: before.ownerId,
      agency: before.agency,
      preparedForRecruitment: true,
    };
    next = { ...next, needs: [...next.needs, need] };
    if (label === "Nouveau besoin") task("qualify-need", "Compléter et qualifier le besoin", 1, "need", {
      needId: need.id,
    });
  }
  if (label === "Nouveau besoin") {
    setStatus("Opportunité ouverte");
    const priorIds=new Set(state.needs.map(need=>need.id));
    const created=next.needs.find(need=>!priorIds.has(need.id));
    if(created) {
      const missingFields=['job','qty','type','start','duration'].filter(name=>!created[name]);
      next={...next,needs:next.needs.map(need=>need.id===created.id?{...need,missingFields,preparedForRecruitment:true,status:missingFields.length?'à qualifier':need.status}:need)};
      client.qualificationWarning=missingFields.length?'Informations du besoin à compléter : '+missingFields.map(name=>({job:'métier',qty:'nombre',type:'contrat',start:'démarrage',duration:'durée'}[name])).join(', '):'';
    }
  } else if (label === "Demande de devis") {
    setStatus("Devis en cours");
    task("prepare-quote", "Préparer le devis", 1, "quote", {
      job: data.job || "",
      qty: data.qty ? Number(data.qty) : null,
      needId: next.needs.find(need => !state.needs.some(previous => previous.id === need.id))?.id,
      type: data.type || "", start: data.start || "", duration: data.duration || "",
      hours: data.hours || "", urgency: data.urgency || "", constraints: data.constraints || "",
    });
  } else if (label === "Devis envoyé") {
    setStatus("Devis envoyé");
    client.lastQuoteDeclaredAt = data.sentAt || stamp;
    task("follow-quote", "Relancer le devis envoyé", rules.quoteDays, "quote", {
      quoteId: data.quoteId || null,
    });
  } else if (label === "Devis accepté") {
    setStatus("Opportunité ouverte");
    client.quoteAcceptanceDeclaredAt = stamp;
    task(
      "verify-quote-acceptance",
      "Vérifier l’acceptation et la signature du devis",
      1,
      "quote",
    );
  } else if (label === "Besoin futur") {
    client.futureNeedDate = data.futureDate || "";
    client.futureNeedPeriod = data.futurePeriod || "";
    setStatus("À relancer");
    const due = data.futureDate
      ? addBusinessDays(data.futureDate, -rules.futureLeadDays, closures)
      : addBusinessDays(today, 30, closures);
    task("future-need", "Recontacter avant le besoin futur", 30, "follow", {});
    if (data.futureDate)
      tasks = tasks.map((t) =>
        t.resultId === result.id
          ? {
              ...t,
              due_date_original:
                due < today ? addBusinessDays(today, 0, closures) : due,
              scheduled_date:
                due < today ? addBusinessDays(today, 0, closures) : due,
            }
          : t,
      );
  } else if (label === "Plaquette envoyée") {
    const duplicate = (state.events || []).some(
      (e) =>
        e.clientId === clientId &&
        e.type === "brochure_sent" &&
        String(e.at || e.createdAt || "").slice(0, 10) === today &&
        e.channel === data.channel,
    );
    if (!duplicate)
      next = {
        ...next,
        events: [
          ...next.events,
          {
            id: crypto.randomUUID(),
            clientId,
            type: "brochure_sent",
            channel: data.channel,
            at: stamp,
            createdAt: stamp,
            declared: true,
          },
        ],
      };
    task("brochure-follow", "Relancer après la plaquette", rules.brochureDays);
  } else if (unanswered.includes(label)) {
    const attempts = Number(before.unansweredAttempts || 0) + 1;
    client.unansweredAttempts = attempts;
    client.callAttempts = Number(before.callAttempts || 0) + 1;
    if (attempts < rules.maxAttempts)
      task(
        "unanswered",
        "Nouvelle tentative de contact",
        rules.unansweredDays * (attempts >= 3 ? 3 : 1),
      );
    else {
      tasks = tasks.map((t) =>
        t.clientId === clientId && t.automationKey === "unanswered" && !t.done
          ? {
              ...t,
              done: true,
              cancelled: true,
              cancelReason: "Limite de tentatives atteinte",
            }
          : t,
      );
      task(
        "find-contact-after-attempts",
        "Vérifier les coordonnées après plusieurs tentatives",
        1,
        "contact",
      );
    }
  } else if (label === "Contact parti") {
    client.contacts = (client.contacts || []).map((c) =>
      (data.contactId ? c.id === data.contactId : c.primary)
        ? {
            ...c,
            active: false,
            primary: false,
            inactiveReason: "Contact parti",
            inactiveAt: stamp,
          }
        : c,
    );
    client.contact = "";
    client.phone = "";
    task("new-decider", "Identifier le nouveau décideur", 1, "contact");
  } else if (
    ["Mauvais numéro", "Numéro hors service", "Email invalide"].includes(label)
  ) {
    const email = label === "Email invalide";
    client.contacts = (client.contacts || []).map((c) =>
      (data.contactId ? c.id === data.contactId : c.primary)
        ? {
            ...c,
            [email ? "emailInvalid" : "phoneInvalid"]: true,
            [email ? "email" : "phone"]: "",
          }
        : c,
    );
    client[email ? "email" : "phone"] = "";
    task(
      "find-coordinates",
      "Rechercher des coordonnées valides",
      1,
      "contact",
    );
  } else if (label === "À rappeler" || label === "Rendez-vous obtenu") {
    setStatus("À relancer");
    task(
      label === "À rappeler" ? "promised-call" : "appointment",
      label === "À rappeler" ? "Rappeler le contact" : "Rendez-vous client",
      0,
      label === "À rappeler" ? "follow" : "appointment",
      { promised: true },
    );
  } else if (
    [
      "Travaille avec un concurrent",
      "Contrat exclusif avec un concurrent",
    ].includes(label)
  ) {
    client.competitor = {
      name: data.competitor || "",
      exclusive:
        label.startsWith("Contrat exclusif") ||
        data.exclusive === true ||
        data.exclusive === "oui",
      renewal: data.renewal || "",
      satisfaction: data.satisfaction || "",
    };
    task("competitor-recovery", "Préparer la reconquête du client", 30);
  } else if (label === "Tarifs trop élevés") {
    client.priceObjection = {
      price: data.competitorPrice || null,
      notes: data.notes || "",
      at: stamp,
    };
    if (data.managerTask)
      task(
        "price-validation",
        "Valider la demande tarifaire — aucune remise automatique",
        1,
        "manager",
      );
    else
      task(
        "price-follow",
        "Recontacter après objection tarifaire",
        rules.noNeedDays,
      );
  } else if (label === "Entreprise en redressement judiciaire") {
    client.legalRisk = "Redressement judiciaire à vérifier";
    client.engagementApprovalRequired = true;
    setStatus("À vérifier");
    task(
      "legal-review",
      "Vérifier la situation juridique avant engagement",
      1,
      "manager",
    );
  } else if (label === "Entreprise en liquidation judiciaire") {
    client.legalRisk = "Liquidation judiciaire à vérifier";
    client.engagementBlocked = true;
    setStatus("À vérifier");
    task("legal-review", "Vérifier la liquidation déclarée", 1, "manager");
  } else if (
    excluded.includes(label) ||
    (label === "Refus de collaboration" && data.opposition)
  ) {
    client.prospectingExcluded = excluded.includes(label);
    client.prospectingOpposition = !!data.opposition;
    client.exclusionReason = data.opposition
      ? "Opposition à la prospection"
      : label;
    client.exclusionAt = stamp;
    client.commercialStatus = "Exclu";
    client.score = 0;
    tasks = tasks.map((t) =>
      t.clientId === clientId && !t.done && t.category !== "manager"
        ? {
            ...t,
            done: true,
            cancelled: true,
            cancelReason: client.exclusionReason,
            completedAt: stamp,
          }
        : t,
    );
    client.nextAction = "";
    client.next_action_date = "";
  } else if (
    [
      "Doublon CRM",
      "Données erronées",
      "Entreprise à vérifier",
      "Entreprise déménagée",
    ].includes(label)
  ) {
    setStatus("À vérifier");
    task(
      "review-establishment",
      label === "Doublon CRM"
        ? "Examiner le doublon — fusion contrôlée requise"
        : "Vérifier la fiche entreprise",
      1,
      "manager",
    );
  } else {
    if (label === "Pas de besoin actuellement") setStatus("En veille");
    else if (label === "Client perdu") client.commercialStatus = "Client perdu";
    else setStatus("À relancer");
    task(
      "commercial-follow",
      label === "Activité en baisse"
        ? "Suivi de l’activité"
        : label === "Client à réactiver"
          ? "Réactiver le client"
          : label === "Rendez-vous demandé"
            ? "Fixer le rendez-vous"
            : "Reprendre contact",
      label === "Activité en baisse" ? rules.decliningDays : rules.noNeedDays,
    );
  }
  if (!unanswered.includes(label)) client.unansweredAttempts = 0;
  client.lastObjection = resultGroups[
    "Absence de besoin et objections"
  ].includes(label)
    ? label
    : client.lastObjection || "";
  if ((client.history || []).length === (before.history || []).length)
    client.history = [
      ...(client.history || []),
      `${today} — ${label}${data.notes ? " : " + data.notes : ""}`,
    ];
  if (data.doNotRecall === true) {
    result.doNotRecall = true;
    client.prospectingExcluded = true;
    client.exclusionReason = "Ne plus rappeler";
    client.exclusionAt = stamp;
    client.commercialStatus = "Exclu";
    client.score = 0;
    client.nextAction = "";
    client.next_action_date = "";
    tasks = tasks.map(t => t.clientId === clientId && !t.done && t.category !== "manager" ? {...t,done:true,cancelled:true,cancelReason:"Ne plus rappeler",completedAt:stamp} : t);
  }
  // A completed call supersedes earlier automatic calls, not unrelated manual work.
  const superseded = tasks.filter(t => t.clientId === clientId && !t.done && !t.cancelled &&
    !t.manuallyModified && !t.manualEditedAt && !Number(t.postponement_count) &&
    (t.reactivationEngine || (t.automationKey && ["call", "follow"].includes(t.category))) && t.resultId !== result.id);
  const supersededIds = new Set(superseded.map(t => t.id));
  tasks = tasks.map(t => supersededIds.has(t.id) ? {...t,done:true,cancelled:true,cancelReason:"Remplacé par le dernier appel",completedAt:stamp} : t);
  for (const field of ["nextAction", "next_action_date"]) {
    const date = String(client[field] || "").slice(0,10);
    if (date && superseded.some(t => String(t.scheduled_date || t.due_date_original).slice(0,10) === date) &&
      !tasks.some(t => t.clientId === clientId && !t.done && !t.cancelled && String(t.scheduled_date || t.due_date_original).slice(0,10) === date)) client[field] = "";
  }
  if (label !== "Client perdu") client.status = before.status;
  return {
    ...next,
    clients: next.clients.map((c) => (c.id === clientId ? client : c)),
    tasks,
  };
}
export function whyCall(client, state) {
  const signals = (state.signals || [])
    .filter((s) => s.clientId === client.id)
    .sort((a, b) =>
      String(b.date || b.createdAt || "").localeCompare(
        String(a.date || a.createdAt || ""),
      ),
    );
  const needs = (state.needs || []).filter(
    (n) => n.clientId === client.id && !["perdu", "signé"].includes(n.status),
  );
  return {
    lastInteraction: client.lastContactAt || client.lastCallAt || "",
    lastResult: client.lastCallResult || "",
    lastObjection: client.lastObjection || "",
    latestOffer: signals[0] || null,
    opportunities: needs,
    attempts: client.callAttempts || 0,
    recommendation: needs.length
      ? "Qualifier et suivre le besoin détecté"
      : client.lastCallResult === "Pas de réponse"
        ? "Nouvelle tentative de contact"
        : "Qualifier les besoins et le décideur",
  };
}
export function crmV2Indicators(state, today = todayParis()) {
  const results = (state.results || []).filter(row=>!row.voidedAt),
    needs = state.needs || [],
    quotes = state.quotes || [];
  const ratio = (numerator, denominator) => ({
    numerator,
    denominator,
    rate: denominator ? Math.round((100 * numerator) / denominator) : null,
  });
  const useful = results.filter((r) => r.deciderJoined).length,
    sent = quotes.filter((q) => q.sentAt).length,
    signed = quotes.filter((q) => q.signedAt).length;
  return {
    calls: results.length,
    useful,
    needs: needs.length,
    quotesSent: sent,
    quotesSigned: signed,
    conversion: {
      contacts: ratio(useful, results.length),
      needs: ratio(needs.length, useful),
      quotes: ratio(sent, needs.length),
      signatures: ratio(signed, sent),
    },
    lateTasks: (state.tasks || []).filter(
      (t) => !t.done && !t.cancelled && (t.due_date_original || "9999") < today,
    ),
    neverContacted: state.clients.filter(
      (c) => !c.lastCallResult && !isBlockedForProspecting(c),
    ),
    withoutFollowup: needs.filter(
      (n) =>
        !(state.tasks || []).some((t) => t.clientId === n.clientId && !t.done),
    ),
    excluded: state.clients.filter(isBlockedForProspecting),
  };
}
export function weightedCommercialScore(client, state, today = todayParis()) {
  const weights = state.scoreWeights || {};
  if (!Object.keys(weights).length) return null;
  if (isBlockedForProspecting(client))
    return {
      score: 0,
      raw: 0,
      capped: false,
      factors: [
        { points: 0, label: "Exclusion ou opposition à la prospection" },
      ],
    };
  const needs = (state.needs || []).filter((n) => n.clientId === client.id),
    signals = (state.signals || []).filter((s) => s.clientId === client.id);
  const recent = signals.some((s) => {
    const age =
      Date.parse(today) -
      Date.parse(String(s.date || s.publishedAt || "").slice(0, 10));
    return age >= 0 && age <= 7 * 86400000;
  });
  const jobs = new Map();
  for (const need of needs) {
    const job = key(need.job);
    if (job) jobs.set(job, (jobs.get(job) || 0) + 1);
  }
  const rules = [
    ["recentOffer", recent, "Offre de recrutement de moins de 7 jours"],
    [
      "volume",
      needs.some((n) => Number(n.qty) > 1),
      "Plusieurs personnes recherchées",
    ],
    [
      "recurrence",
      [...jobs.values()].some((n) => n >= 2),
      "Besoins distincts récurrents pour un métier",
    ],
    [
      "jobMatch",
      needs.some((n) =>
        (state.coveredJobs || []).some((job) => key(job) === key(n.job)),
      ),
      "Métier explicitement couvert par l’agence",
    ],
    [
      "location",
      !!(client.city || client.postCode),
      "Localisation de l’établissement renseignée",
    ],
    [
      "collaboration",
      (client.orders || []).length > 0,
      "Historique de collaboration",
    ],
    [
      "positiveReply",
      (state.results || []).some(
        (r) =>
          r.clientId === client.id &&
          [
            "Intéressé / à recontacter",
            "Rendez-vous demandé",
            "Rendez-vous obtenu",
          ].includes(r.label),
      ),
      "Réponse commerciale positive",
    ],
    [
      "quote",
      (state.quotes || []).some(
        (q) =>
          q.clientId === client.id && q.sentAt && !q.signedAt && !q.refusedAt,
      ),
      "Devis en attente de décision",
    ],
    [
      "futureNeed",
      !!client.futureNeedDate &&
        client.futureNeedDate >= today &&
        Date.parse(client.futureNeedDate) - Date.parse(today) <= 30 * 86400000,
      "Besoin futur dans les 30 jours",
    ],
  ];
  const factors = rules
    .filter(([, condition]) => condition)
    .map(([name, , label]) => ({ points: Number(weights[name]) || 0, label }));
  const raw = factors.reduce((sum, f) => sum + f.points, 0);
  return {
    score: Math.max(0, Math.min(100, raw)),
    raw,
    capped: raw > 100,
    factors,
  };
}
export function missionInactivity(client, today = todayParis()) {
  if (client.kind === "prospect") return null;
  const dates = [];
  let planned = false;
  for (const mission of [
    ...(client.orders || []),
    ...(client.contracts || []),
  ]) {
    if (/annul|cancel/i.test(mission.status || "")) continue;
    const raw = [
      mission.endDate,
      mission.missionEndDate,
      ...(mission.dateRanges || []).map(
        (period) => period.endDate || period.startDate || period.date,
      ),
      ...(mission.selectedDates || []),
      ...(mission.missionDates || []),
    ];
    if (!mission.apiOrder && mission.date) raw.push(mission.date);
    for (const value of raw) {
      const date = String(value?.date || value || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(date)))
        continue;
      if (date >= today) planned = true;
      else dates.push(date);
    }
  }
  if (planned || !dates.length) return null;
  const lastMissionDate = dates.sort().at(-1),
    days = Math.floor(
      (Date.parse(today) - Date.parse(lastMissionDate)) / 86400000,
    );
  return {
    lastMissionDate,
    days,
    seasonal: !!client.seasonal,
    knownFutureNeed: !!client.futureNeedDate && client.futureNeedDate >= today,
  };
}
export function applyInactivityRules(state, today = todayParis()) {
  let tasks = [...(state.tasks || [])];
  const clients = state.clients.map((client) => {
    const inactivity = missionInactivity(client, today);
    if (!inactivity || isBlockedForProspecting(client) || isReactivationDeferred(client, today)) return client;
    const thresholds = {
      warning: 15,
      reactivation: 30,
      recovery: 60,
      manager: 90,
      ...(state.inactivityThresholds || {}),
      ...(client.inactivityThresholds || {}),
    };
    const stage =
      inactivity.days >= thresholds.manager
        ? "manager"
        : inactivity.days >= thresholds.recovery
          ? "recovery"
          : inactivity.days >= thresholds.reactivation
            ? "reactivation"
            : inactivity.days >= thresholds.warning
              ? "warning"
              : null;
    if (!stage) return client;
    if (
      !inactivity.seasonal &&
      !inactivity.knownFutureNeed &&
      stage !== "warning"
    ) {
      const category = stage === "manager" ? "manager" : "follow",
        rule = `inactivity:${client.id}:${inactivity.lastMissionDate}:${category}`;
      if (!tasks.some((task) => task.automationKey === rule && !task.done))
        tasks.push({
          id: crypto.randomUUID(),
          clientId: client.id,
          ownerId: client.ownerId,
          agency: client.agency,
          title:
            stage === "manager"
              ? `Client inactif depuis ${thresholds.manager} jours : examen responsable`
              : "Réactiver le client sans mission depuis " +
                inactivity.days +
                " jours",
          category,
          automationKey: rule,
          due_date_original: addBusinessDays(
            today,
            0,
            state.calendar?.closures || state.closures || [],
          ),
          scheduled_date: addBusinessDays(
            today,
            0,
            state.calendar?.closures || state.closures || [],
          ),
          done: false,
          createdAt: new Date().toISOString(),
          reactivationEngine: true,
        });
    }
    return {
      ...client,
      inactivityAlert: { ...inactivity, stage },
      commercialStatus:
        inactivity.seasonal || inactivity.knownFutureNeed
          ? client.commercialStatus
          : "Client inactif",
    };
  });
  return { ...state, clients, tasks };
}

// A recorded call takes precedence over inactivity until its planned callback.
export function isReactivationDeferred(client, today = todayParis()) {
  const due = String(client.next_action_date || client.nextAction || "").slice(0, 10);
  return Boolean(client.lastCallAt && /^\d{4}-\d{2}-\d{2}$/.test(due) && due > today);
}
